import { useCallback, useEffect, useRef, useState } from 'react';

import { restoredSendState, type SendState } from './send-state';

export interface SendAttempt {
  update: (state: SendState) => void;
  assertCurrent: () => void;
}

const createSendAttempt = (
  isCurrent: () => boolean,
  setState: (state: SendState) => void,
): SendAttempt => ({
  update: (next) => {
    if (isCurrent()) {
      setState(next);
    }
  },
  assertCurrent: () => {
    if (!isCurrent()) {
      throw new Error('Send canceled.');
    }
  },
});

export function useSendAttempts(senderAddress: string | undefined) {
  const [state, setState] = useState<SendState>(() => restoredSendState(senderAddress));
  const versionRef = useRef(0);

  const beginAttempt = useCallback((): SendAttempt => {
    const version = ++versionRef.current;
    return createSendAttempt(() => version === versionRef.current, setState);
  }, []);

  const reset = useCallback(() => {
    versionRef.current += 1;
    setState(restoredSendState(senderAddress));
  }, [senderAddress]);

  useEffect(() => {
    reset();

    return () => {
      versionRef.current += 1;
    };
  }, [reset]);

  return { state, beginAttempt, reset };
}
